import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface UploadedFile {
  id: string;
  name: string;
  url?: string;
  fileData?: string;
  fileSize?: string;
  finalSummary?: string;
  extractedData?: any[];
  env?: string;
  fileName?: string;
  fileUrl?: string;
  file?: any;
  filePath?: string;
}

export interface SelectedContext {
  id: string;
  name: string;
  use_case_title?: string;
  field_type?: string;
  content?: string;
  context_knowledge?: string;
}

export interface Taxonomy {
  domain: string;
  function: string;
  process_group: string;
  process: string;
}

export interface ProposedEnhancementItem {
  id?: string;
  enhancement_title: string;
  enhancement_description: string;
}

export const USE_DATA_XPLR_FIELDS = [
  'currentProcess',
  'expectedImpact',
  'specificIdeasOfImprovement',
  'keyPerformanceIndicators',
] as const;

export type UseDataXplrFieldKey = (typeof USE_DATA_XPLR_FIELDS)[number];

type UseDataXplrFieldMap = Record<UseDataXplrFieldKey, boolean>;

export interface LinkedSession {
  id: string;
  use_case_title: string;
  session_name?: string;
  created_by: string;
  created_at: string;
  conversation_id: string;
  field_type?: string;
  semantic_knowledge?: any;
  ideation_intake?: any[];
}

export interface NewIdeationState {
  id: string;
  currentProcess: string;
  expectedImpact: string;
  specificIdeasOfImprovement: string;
  keyPerformanceIndicators: string;
  domain: string;
  selectedFunction: string;
  selectedFunctionId: string;
  selectedProcessGroup: string;
  selectedProcessGroupId: string;
  selectedProcess: string;
  selectedProcessId: string;
  technologies: string[];
  status: string;
  uploadedFiles: UploadedFile[];
  selectedContexts: SelectedContext[];
  detailsText: string;
  taxonomy: Taxonomy;
  finalSummary: string;
  prefillUseCaseId: string | null;
  isPrefillViewMode: boolean;
  cumulativeFieldData?: any;
  useDataXplrFields: UseDataXplrFieldMap;
  recent_imported_chat?: Record<string, any>;
  showRegenerateButton: boolean;
  cardToBeRegeneratedId: string | null;
  // Allows other screens (e.g. the ideation dashboard tiles) to reflect status changes immediately
  // without waiting for a refetch/lifecycle trigger.
  useCaseStatusOverrides: Record<string, string>; // key: use_case_id
  proposedEnhancements: ProposedEnhancementItem[];
  linkedSessions: Record<string, LinkedSession[]>; // key: field name, value: array of linked sessions
}

const initialState: NewIdeationState = {
  id: '',
  currentProcess: '',
  expectedImpact: '',
  specificIdeasOfImprovement: '',
  keyPerformanceIndicators: '',
  domain: '',
  selectedFunction: '',
  selectedFunctionId: '',
  selectedProcessGroup: '',
  selectedProcessGroupId: '',
  selectedProcess: '',
  selectedProcessId: '',
  technologies: [],
  status: 'draft',
  uploadedFiles: [],
  selectedContexts: [],
  detailsText: '',
  taxonomy: {
    domain: '',
    function: '',
    process_group: '',
    process: '',
  },
  finalSummary: '',
  prefillUseCaseId: null,
  isPrefillViewMode: false,
  cumulativeFieldData: [],
  useDataXplrFields: USE_DATA_XPLR_FIELDS.reduce<UseDataXplrFieldMap>((acc, field) => {
    acc[field] = false;
    return acc;
  }, {} as UseDataXplrFieldMap),
  recent_imported_chat: {
    current_process_chat: {
      chat_id: '',
      version_id: '',
    },
    expected_impact_chat: {
      chat_id: '',
      version_id: '',
    },
    specific_ideas_of_improvement_chat: {
      chat_id: '',
      version_id: '',
    },
    key_performance_indicators_chat: {
      chat_id: '',
      version_id: '',
    },
  },
  showRegenerateButton: false,
  cardToBeRegeneratedId: null,
  useCaseStatusOverrides: {},
  proposedEnhancements: [],
  linkedSessions: {}, // initialize as empty object
};

type UpdateIdeationFieldPayload<K extends keyof NewIdeationState = keyof NewIdeationState> = {
  field: K;
  value: NewIdeationState[K];
};

const newIdeationSlice = createSlice({
  name: 'newIdeation',
  initialState,
  reducers: {
    updateIdeationFieldState: (state, action: PayloadAction<UpdateIdeationFieldPayload>) => {
      const { field, value } = action.payload;
      (state as Record<string, unknown>)[field] = value;
    },
    setOpportunityDetails: (state, action: PayloadAction<Partial<NewIdeationState>>) => {
      console.log('setOpportunityDetails dispatched with payload:', action.payload);
      return {
        ...state,
        ...action.payload,
      };
    },
    resetOpportunityDetails: (state) => {
      // Preserve cross-screen UI overrides (e.g. dashboard tile status) even when the ideation detail
      // panel is closed/reset.
      return {
        ...initialState,
        useCaseStatusOverrides: state.useCaseStatusOverrides,
      };
    },
    setPrefillUseCaseId: (state, action: PayloadAction<string | null>) => {
      state.prefillUseCaseId = action.payload;
    },
    setPrefillViewMode: (state, action: PayloadAction<boolean>) => {
      state.isPrefillViewMode = action.payload;
    },
    markUseDataXplrField: (state, action: PayloadAction<UseDataXplrFieldKey>) => {
      state.useDataXplrFields[action.payload] = true;
    },
    clearUseDataXplrField: (state, action: PayloadAction<UseDataXplrFieldKey>) => {
      state.useDataXplrFields[action.payload] = false;
    },
    setShowRegenerateButton: (state, action: PayloadAction<boolean>) => {
      state.showRegenerateButton = action.payload;
    },
    setCardToBeRegeneratedId: (state, action: PayloadAction<string | null>) => {
      state.cardToBeRegeneratedId = action.payload;
    },
    setUseCaseStatusOverride: (
      state,
      action: PayloadAction<{ useCaseId: string; status: string }>
    ) => {
      state.useCaseStatusOverrides[action.payload.useCaseId] = action.payload.status;
    },
    clearUseCaseStatusOverride: (state, action: PayloadAction<string>) => {
      delete state.useCaseStatusOverrides[action.payload];
    },
    setProposedEnhancements: (state, action: PayloadAction<ProposedEnhancementItem[]>) => {
      state.proposedEnhancements = action.payload;
    },
    addLinkedSessions: (state, action: PayloadAction<{ field: string; sessions: LinkedSession[] }>) => {
      const { field, sessions } = action.payload;
      if (!state.linkedSessions[field]) {
        state.linkedSessions[field] = [];
      }
      // Add only sessions that are not already linked to this field
      const existingIds = new Set(state.linkedSessions[field].map(s => s.id));
      const newSessions = sessions.filter(s => !existingIds.has(s.id));
      state.linkedSessions[field].push(...newSessions);
    },
    removeLinkedSession: (state, action: PayloadAction<{ field: string; sessionId: string }>) => {
      const { field, sessionId } = action.payload;
      if (state.linkedSessions[field]) {
        state.linkedSessions[field] = state.linkedSessions[field].filter(s => s.id !== sessionId);
      }
    },
    clearLinkedSessions: (state, action: PayloadAction<{ field: string }>) => {
      const { field } = action.payload;
      state.linkedSessions[field] = [];
    },
  },
});

export const {
  setOpportunityDetails,
  updateIdeationFieldState,
  resetOpportunityDetails,
  setPrefillUseCaseId,
  setPrefillViewMode,
  markUseDataXplrField,
  clearUseDataXplrField,
  setShowRegenerateButton,
  setCardToBeRegeneratedId,
  setUseCaseStatusOverride,
  clearUseCaseStatusOverride,
  setProposedEnhancements,
  addLinkedSessions,
  removeLinkedSession,
  clearLinkedSessions,
} = newIdeationSlice.actions;

export default newIdeationSlice.reducer;
